import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Severity,
  type SeverityType,
} from '@kirin/api-client/contracts/shared/severity';
import { type JSX, useState } from 'react';
import { SEVERITY_OPTIONS } from '../policylist/helpers';

export interface RuleFormValues {
  pattern: string;
  description: string;
  severity: SeverityType;
}

interface RegexEditComponentProps {
  inputLabel?: string;
  descriptionLabel?: string;
  severityLabel?: string;
  /** Pre-populate the form (edit mode). Omit for an empty add form. */
  initialValues?: RuleFormValues;
  /** Submit button text. Defaults to "Add Rule"; pass "Edit Rule" when editing. */
  submitLabel?: string;
  /** Focus the Rule field on mount (e.g. when an Edit drawer opens). */
  autoFocus?: boolean;
  onSubmit: (rule: RuleFormValues) => void;
  onCancel: () => void;
  disabled?: boolean;
  /** Support role: keep the form and Cancel interactive, only disable Submit. */
  submitDisabled?: boolean;
}

export function RegexEditComponent({
  inputLabel = 'Rule',
  descriptionLabel = 'Description',
  severityLabel = 'Severity',
  initialValues,
  submitLabel = 'Add Rule',
  autoFocus = false,
  onSubmit,
  onCancel,
  disabled = false,
  submitDisabled = false,
}: RegexEditComponentProps): JSX.Element {
  const [pattern, setPattern] = useState(initialValues?.pattern ?? '');
  const [description, setDescription] = useState(
    initialValues?.description ?? ''
  );
  const [severity, setSeverity] = useState<SeverityType>(
    initialValues?.severity ?? Severity.Medium
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate rule (regex)
    if (!pattern.trim()) {
      newErrors.pattern = 'Rule is required';
    } else if (pattern.length > 500) {
      newErrors.pattern = 'Rule must be 500 characters or less';
    } else {
      // Validate that the rule is a syntactically valid regular expression
      try {
        new RegExp(pattern);
      } catch {
        newErrors.pattern = 'Invalid regular expression';
      }
    }

    // Validate description
    if (!description.trim()) {
      newErrors.description = 'Description is required';
    } else if (description.length > 200) {
      newErrors.description = 'Description must be 200 characters or less';
    }

    // Validate severity
    if (!severity) {
      newErrors.severity = 'Severity is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (): void => {
    if (validateForm()) {
      onSubmit({
        pattern: pattern.trim(),
        description: description.trim(),
        severity,
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Rule Input */}
      <div className="space-y-2">
        <Label htmlFor="pattern">{inputLabel}</Label>
        <Input
          id="pattern"
          value={pattern}
          onChange={e => setPattern(e.target.value)}
          placeholder="e.g., eval\\s*\\("
          className={errors.pattern ? 'border-red-500' : ''}
          disabled={disabled}
          maxLength={500}
          autoFocus={autoFocus}
        />
        {errors.pattern && (
          <div className="text-xs text-red-500">{errors.pattern}</div>
        )}
      </div>

      {/* Description Input */}
      <div className="space-y-2">
        <Label htmlFor="description">{descriptionLabel}</Label>
        <Textarea
          id="description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Brief description..."
          className={cn(
            'min-h-9 resize-none',
            errors.description && 'border-red-500'
          )}
          disabled={disabled}
          maxLength={200}
          rows={1}
        />
        {errors.description && (
          <div className="text-xs text-red-500">{errors.description}</div>
        )}
      </div>

      {/* Severity Select */}
      <div className="space-y-2">
        <Label htmlFor="severity">{severityLabel}</Label>
        <Select
          value={severity}
          onValueChange={value => setSeverity(value as SeverityType)}
          disabled={disabled}
        >
          <SelectTrigger id="severity" className="w-full">
            <SelectValue placeholder="Select severity" />
          </SelectTrigger>
          <SelectContent>
            {SEVERITY_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.severity && (
          <div className="text-xs text-red-500">{errors.severity}</div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 justify-end pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={disabled}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || submitDisabled}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
